import { useState } from 'react';
import { ChartLine, ClockCountdown, Info, Megaphone } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import { cn } from '@/lib/utils';
import { ReportAlertStrip } from '../components/report-alert-strip';
import { ReportDelta } from '../components/report-delta';
import { ReportEngagementChart } from '../components/report-engagement-chart';
import { ReportExportControl } from '../components/report-export-control';
import { ReportKpiTile } from '../components/report-kpi-tile';
import { ReportRangeControl } from '../components/report-range-control';
import { DeliveryDataNotice, ReportRequestState } from '../components/report-request-state';
import {
  isSentOverviewCampaign,
  ReportsCampaignsTable,
} from '../components/reports-campaigns-table';
import { SenderHealthCard } from '../components/sender-health-card';
import { useReportOverview } from '../hooks/use-analytics';
import { useReportRange } from '../hooks/use-report-range';
import { fmtDateTime, fmtNum, fmtPct } from '../utils/report-format';
import { OPEN_CAVEAT } from '../utils/report-rates';

export function ReportsOverviewPage() {
  const { range } = useReportRange();
  const [interval, setInterval] = useState<'day' | 'week'>('day');
  const invalidRange = range.from > range.to;
  const overview = useReportOverview({
    from: range.from,
    to: range.to,
    interval,
  });

  const data = overview.data;
  const sentCount = data?.campaigns.filter((row) => isSentOverviewCampaign(row.status)).length ?? 0;

  return (
    <div className="flex h-full flex-col gap-5 overflow-y-auto p-4 sm:p-6">
      <div>
        <h1 className="text-2xl font-semibold text-neutral-900">Reports overview</h1>
        <p className="mt-0.5 text-neutral-600">
          How sending is going across every campaign in range. Open a campaign for its own report.
        </p>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
          <ReportRangeControl />
          <IntervalToggle value={interval} onChange={setInterval} />
          {data?.lastEventAt ? (
            <p className="inline-flex min-h-[18px] items-center gap-1.5 text-xs text-neutral-500">
              <ClockCountdown className="size-3.5 shrink-0" aria-hidden />
              <span>
                Counts as of {fmtDateTime(data.lastEventAt)} · from {fmtNum(sentCount)} campaign
                {sentCount === 1 ? '' : 's'} sent in range
              </span>
            </p>
          ) : null}
        </div>
        <ReportExportControl
          className="ml-auto shrink-0"
          report="overview"
          getFilters={() => ({ from: range.from, to: range.to, interval })}
        />
      </div>

      <ReportRequestState
        invalidRange={invalidRange}
        isLoading={overview.isLoading}
        error={overview.error}
        skeleton="overview"
      />

      {data && data.campaigns.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-neutral-200 bg-white p-10 text-center shadow-sm">
          <ChartLine className="size-8 text-neutral-500" aria-hidden />
          <div className="text-base font-semibold text-neutral-900">
            No campaigns sent in this range
          </div>
          <p className="max-w-[380px] text-neutral-600">
            Nothing finished sending in the selected window. Try a wider range, or send your first
            campaign.
          </p>
          <Button asChild className="gap-2">
            <Link to="/campaigns">
              <Megaphone className="size-4" aria-hidden />
              Go to campaigns
            </Link>
          </Button>
        </div>
      ) : null}

      {data && data.campaigns.length > 0 ? <OverviewBody data={data} days={range.days} /> : null}
    </div>
  );
}

function IntervalToggle({
  value,
  onChange,
}: {
  value: 'day' | 'week';
  onChange: (next: 'day' | 'week') => void;
}) {
  return (
    <div
      className="inline-flex items-center overflow-hidden rounded-md border border-neutral-300 bg-white"
      role="group"
      aria-label="Chart interval"
    >
      {(
        [
          { key: 'day' as const, label: 'Day' },
          { key: 'week' as const, label: 'Week' },
        ] as const
      ).map((option, index) => (
        <button
          key={option.key}
          type="button"
          aria-pressed={value === option.key}
          onClick={() => onChange(option.key)}
          className={cn(
            'px-3 py-1.5 text-[13px] font-medium',
            index > 0 && 'border-l border-neutral-300',
            value === option.key
              ? 'bg-primary-600 text-white'
              : 'bg-white text-neutral-700 hover:bg-neutral-50'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function OverviewBody({
  data,
  days,
}: {
  data: NonNullable<ReturnType<typeof useReportOverview>['data']>;
  days: number;
}) {
  const deltas = data.comparison?.deltas;
  const complaintFlag =
    data.rates.complaintRate != null && data.rates.complaintRate >= data.thresholds.complaintRate;
  const deliveryValue = data.deliveryDataAvailable ? data.rates.deliveryRate : null;

  return (
    <>
      {data.deliveryDataAvailable ? null : <DeliveryDataNotice />}
      <ReportAlertStrip alerts={data.alerts} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <ReportKpiTile
          label="Delivered"
          value={data.deliveryDataAvailable ? fmtNum(data.totals.delivered) : '—'}
          hint={
            data.deliveryDataAvailable
              ? `${fmtNum(data.totals.sent)} sent · ${fmtPct(deliveryValue)} delivered`
              : `${fmtNum(data.totals.sent)} sent · delivery data not received yet`
          }
        >
          <div className="mt-2">
            <ReportDelta metricKey="delivered" delta={deltas?.delivered} days={days} />
          </div>
        </ReportKpiTile>
        <ReportKpiTile
          label="Open rate"
          value={fmtPct(data.rates.openRate)}
          hint="Unique opens ÷ delivered"
        >
          <div className="mt-2">
            <ReportDelta
              metricKey="openRate"
              delta={deltas?.openRate}
              days={days}
              asPoints
              current={data.rates.openRate}
            />
          </div>
          <div className="mt-2 flex items-start gap-1.5 text-xs text-neutral-500">
            <Info className="mt-0.5 size-[13px] shrink-0" aria-hidden />
            <span>{OPEN_CAVEAT}</span>
          </div>
        </ReportKpiTile>
        <ReportKpiTile
          label="Click rate"
          value={fmtPct(data.rates.clickRate)}
          hint="Unique clicks ÷ delivered"
        >
          <div className="mt-2">
            <ReportDelta
              metricKey="clickRate"
              delta={deltas?.clickRate}
              days={days}
              asPoints
              current={data.rates.clickRate}
            />
          </div>
        </ReportKpiTile>
        <ReportKpiTile
          label="Spam complaints"
          value={fmtPct(data.rates.complaintRate, 2)}
          hint={`Complaints ÷ delivered · limit ${fmtPct(data.thresholds.complaintRate, 2)}`}
          flagged={complaintFlag}
        >
          <div className="mt-2">
            <ReportDelta
              metricKey="complaintRate"
              delta={deltas?.complaintRate}
              days={days}
              asPoints
              current={data.rates.complaintRate}
            />
          </div>
        </ReportKpiTile>
      </div>

      <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
        <h3 className="mb-1 text-base font-semibold text-neutral-900">
          Opens and clicks over time
        </h3>
        <p className="mb-3 text-xs text-neutral-500">
          Events counted on the day they happened
          {data.period.interval === 'week' ? ', in weeks starting Monday (UTC)' : ''}. Sent volume
          sits on the job’s start day. Every bucket in the range is shown, including zeros.
        </p>
        <ReportEngagementChart series={data.series} />
      </div>

      <ReportsCampaignsTable
        campaigns={data.campaigns}
        days={days}
        complaintThreshold={data.thresholds.complaintRate}
        deliveryDataAvailable={data.deliveryDataAvailable}
      />
      <SenderHealthCard
        rates={data.rates}
        thresholds={data.thresholds}
        days={days}
        deliveryDataAvailable={data.deliveryDataAvailable}
      />
    </>
  );
}
